// /mnt/data/Pasted code.js
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaMicrophone,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaPlayCircle,
  FaNewspaper,
  FaPodcast,
} from "react-icons/fa";

const speakingEvents = [
  {
    title: "Climate Index: Future of Climate Financing in Africa and COP30 Reflections",
    date: "Dec 10, 2025",
    sortDate: "2025-12-10",
    monthBadge: "Dec 2025",
    platform: "Power FM",
    type: "audio",
    embedUrl:
      "https://omny.fm/shows/power-business/climate-index-future-of-climate-financing-in-africa-and-cop30-reflections/embed?style=Cover&media=Audio&size=Wide",
    externalUrl:
      "https://omny.fm/shows/power-business/climate-index-future-of-climate-financing-in-africa-and-cop30-reflections",
    ctaLabel: "Listen on Power FM",
    details:
      "Tehillah Niselow is in conversation with Kenneth Milanzi, Head of Stakeholder Engagement: Strategic Projects at Absa Group.",
    summary:
      "A discussion on the future of climate financing in Africa, the evolving COP30 agenda, and practical financing pathways to unlock credible climate action on the continent.",
    images: [
      "/images/TNPower.jpg",
    "/images/KenzoPower.jpg",
    "/images/KenPower2.jpeg",
  ],
  },
  {
    title:
      "Financing Africa’s Climate Future: Can Sustainable Capital Bridge the COP30 Gap?",
    date: "Dec 4, 2025",
    sortDate: "2025-12-04",
    monthBadge: "Dec 2025",
    platform: "Channel Africa",
    type: "video",
    embedUrl: "https://www.youtube.com/embed/uFaxVkDydqM",
    externalUrl: "https://www.youtube.com/watch?v=uFaxVkDydqM",
    ctaLabel: "Watch on YouTube",
    details:
      "In this episode of Africa In Business, host Thami Ngubeni unpacks one of the biggest pressure points ahead of COP30: climate finance.",
    summary:
      "With negotiations stalled around the NCQG agreed at COP29, the discussion focuses on scalable solutions for developing nations, including credible investment plans, capacity-building, and high-impact climate projects.",
    images: ["/images/CHannelAfrica.jpg"],
  },
  {
    title: "Financing the South's Energy Future",
    date: "Oct 29, 2025",
    sortDate: "2025-10-29",
    monthBadge: "Oct 2025",
    platform: "Observer Research Foundation",
    type: "video",
    embedUrl: "https://www.youtube.com/embed/eKszm2RpcBs",
    externalUrl: "https://www.youtube.com/watch?v=eKszm2RpcBs",
    ctaLabel: "Watch session",
    details:
      "A panel on how Africa and South Asia can sequence energy transitions while powering development, jobs, innovation, and industrial growth.",
    summary:
      "Kenneth Milanzi joins Saliem Fakir, Calum Nicholson, and Stefania Relva in a discussion moderated by Sheryl Foo on green industries, energy security, and financing the transition at scale.",
    images: ["/images/CTC.jpg",
      "/images/CCPanel.jpeg",
      "/images/CCKen.jpeg",      
    ],
  },
  {
    title:
      "Climate Index: Leveraging Transition Finance for a Just Climate Future in the build up to COP30",
    date: "Jul 29, 2025",
    sortDate: "2025-07-29",
    monthBadge: "Jul 2025",
    platform: "Power FM - The Power Business Show",
    type: "audio",
    embedUrl:
      "https://omny.fm/shows/power-business/climate-index-leveraging-transition-finance-for-a-just-climate-future-in-the-build-up-to-cop30/embed?style=Cover&media=Audio&size=Wide",
    externalUrl:
      "https://omny.fm/shows/power-business/climate-index-leveraging-transition-finance-for-a-just-climate-future-in-the-build-up-to-cop30",
    ctaLabel: "Listen to interview",
    details: "Tehillah Niselow in conversation with Kenneth Milanzi.",
    summary:
      "A focused discussion on how transition finance can support a just climate future and help shape practical momentum in the lead-up to COP30.",
    images: ["/images/TNPower.jpg",
      "/images/KenPower1.jpeg",
      "/images/KenPower.jpeg",
    ],
  },
  {
    title: "Owning Your Career",
    date: "Jul 21, 2025",
    sortDate: "2025-07-21",
    monthBadge: "Jul 2025",
    platform: "Geological Society of South Africa",
    type: "video",
    embedUrl: "https://www.youtube.com/embed/0nV1rbjstTY",
    externalUrl: "https://www.youtube.com/watch?v=0nV1rbjstTY",
    ctaLabel: "Watch talk",
    details:
      "Kenneth Milanzi speaks on career control through his journey at the GSSA 2025 Soft Skills for Geoscientists event.",
    summary:
      "A career-focused session covering personal agency, growth, and professional development through the lens of Kenneth’s own journey.",
    images: ["/images/KenzoGSSA.jpg"],
  },
  {
    title:
      "How Transition Finance Can Catalyse Africa's Green Industrial Revolution",
    date: "Nov 7, 2024",
    sortDate: "2024-11-07",
    monthBadge: "Nov 2024",
    platform: "Africa Science News",
    type: "article",
    externalUrl:
      "https://africasciencenews.org/how-transition-finance-can-catalyse-africas-green-industrial-revolution/",
    ctaLabel: "Read article",
    details:
      "The transition finance market provides a unique opportunity for Africa to leapfrog to low-carbon technologies and business models in ways that address climate risks and enhance competitiveness.",
    summary:
      "A thought leadership piece highlighting how transition finance can unlock industrial growth, resilience, and green competitiveness across African markets.",
    images: ["/images/AfricaScienceNews.png"],
  },
  {
    title: "The Synergy Between Corporate Citizenship and Enterprise Development",
    date: "Oct 25, 2024",
    sortDate: "2024-10-25",
    monthBadge: "Oct 2024",
    platform: "Africa Impact Matters",
    type: "link",
    externalUrl:
      "https://www.primediaplus.com/2024/11/11/from-vision-to-reality-stephen-grootes-explores-the-significance-of-esg-sustainable-financing-to-absa",
    ctaLabel: "Explore feature",
    details:
      "Stephen Grootes hosts Kenneth Milanzi and Heidi Barends in a discussion on ESG, sustainable financing, and Absa’s role in balancing commercial priorities with environmental responsibility.",
    summary:
      "A featured podcast/article entry exploring the relationship between corporate citizenship, enterprise development, and practical ESG delivery in financial services.",
    images: ["/images/AIM.png"],
  },
];

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.2 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Speaking = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const sortedEvents = useMemo(
    () =>
      [...speakingEvents].sort(
        (a, b) => new Date(b.sortDate) - new Date(a.sortDate)
      ),
    []
  );

  const openModal = (event) => {
    setSelectedEvent(event);
    setCurrentImageIndex(0);
  };

  const closeModal = () => setSelectedEvent(null);

  const nextImage = () => {
    if (!selectedEvent?.images?.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % selectedEvent.images.length);
  };

  const prevImage = () => {
    if (!selectedEvent?.images?.length) return;
    setCurrentImageIndex(
      (prev) =>
        (prev - 1 + selectedEvent.images.length) % selectedEvent.images.length
    );
  };

  const renderMedia = (event) => {
    if (event.type === "video") {
      return (
        <div className="mt-8">
          <h4 className="mb-3 text-lg font-semibold">Watch</h4>
          <div className="overflow-hidden rounded-lg border border-black shadow-md">
            <div className="aspect-video w-full bg-black">
              <iframe
                src={event.embedUrl}
                title={event.title}
                className="h-full w-full"
                frameBorder="0"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      );
    }

    if (event.type === "audio") {
      return (
        <div className="mt-8">
          <h4 className="mb-3 text-lg font-semibold">Listen</h4>
          <div className="overflow-hidden rounded-lg border border-black shadow-md">
            <iframe
              src={event.embedUrl}
              title={event.title}
              width="100%"
              height="180"
              frameBorder="0"
              allow="autoplay; clipboard-write; fullscreen"
              allowFullScreen
              className="block w-full"
            />
          </div>
        </div>
      );
    }

    if (event.type === "article" || event.type === "link") {
      return (
        <div className="mt-8 rounded-xl border border-black bg-black/5 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-3">
            {event.type === "article" ? (
              <FaNewspaper className="text-xl" />
            ) : (
              <FaPodcast className="text-xl" />
            )}
            <h4 className="text-lg font-semibold">
              {event.type === "article"
                ? "Featured article"
                : "Featured conversation"}
            </h4>
          </div>

          <p className="mb-4 text-sm leading-6 text-black/80">{event.summary}</p>

          <a
            href={event.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-black px-4 py-2 font-medium transition hover:bg-black hover:text-white"
          >
            {event.ctaLabel}
            <FaExternalLinkAlt />
          </a>
        </div>
      );
    }

    return null;
  };

  return (
    <section className="relative overflow-hidden bg-white px-6 py-24 text-black md:px-20">
      <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-black opacity-5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-black opacity-5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <h2 className="mb-6 animate-fadeIn text-3xl font-bold md:text-4xl">
          Public Speaking & Thought Leadership
        </h2>
        <p className="mb-12 animate-fadeIn text-black delay-200 md:text-lg">
          Sharing ideas and inspiring change is a passion. I engage audiences on
          sustainability, ESG, climate finance, career development, and personal
          growth, translating complex concepts into actionable insight.
        </p>
      </div>

      <motion.div
        className="relative z-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {sortedEvents.map((event) => (
          <motion.div
            key={event.title}
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            className="relative cursor-pointer overflow-hidden rounded-xl border border-black bg-white p-6 shadow-md transition hover:shadow-xl"
            onClick={() => openModal(event)}
          >
            <div className="absolute -right-6 -top-6 text-4xl text-black opacity-10">
              {event.type === "video" || event.type === "audio" ? (
                <FaPlayCircle />
              ) : (
                <FaMicrophone />
              )}
            </div>

            <span className="mb-4 inline-block rounded-full border border-black bg-black px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
              {event.monthBadge}
            </span>

            <h3 className="mb-2 text-lg font-semibold">{event.title}</h3>
            <p className="font-bold">{event.date}</p>
            <p className="mt-1">{event.platform}</p>
            <span className="mt-4 inline-block rounded-full border border-black px-3 py-1 text-xs font-medium uppercase tracking-wide">
              {event.type}
            </span>
          </motion.div>
        ))}
      </motion.div>

      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-8 text-black shadow-2xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute right-4 top-4 text-2xl font-bold text-black transition hover:opacity-70"
                aria-label="Close modal"
              >
                &times;
              </button>

              <span className="mb-4 inline-block rounded-full border border-black bg-black px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                {selectedEvent.monthBadge}
              </span>

              <h3 className="mb-2 pr-10 text-2xl font-bold">
                {selectedEvent.title}
              </h3>
              <span className="mb-1 block font-semibold">{selectedEvent.date}</span>
              <p className="mb-2 font-medium text-black/70">
                {selectedEvent.platform}
              </p>
              <p className="mb-6 leading-7">{selectedEvent.details}</p>

              {selectedEvent.images?.length > 0 && (
                <div>
                  <h4 className="mb-3 text-lg font-semibold">Gallery</h4>
                  <div className="relative h-64 w-full overflow-hidden rounded-lg shadow-md md:h-96">
                    <img
                      src={selectedEvent.images[currentImageIndex]}
                      alt={`${selectedEvent.title} ${currentImageIndex + 1}`}
                      className="h-full w-full rounded-lg object-contain"
                    />

                    {selectedEvent.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-black bg-white p-2 text-black transition hover:bg-black hover:text-white"
                          aria-label="Previous image"
                        >
                          <FaChevronLeft />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-black bg-white p-2 text-black transition hover:bg-black hover:text-white"
                          aria-label="Next image"
                        >
                          <FaChevronRight />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {renderMedia(selectedEvent)}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Speaking;